import { AsyncPipe, NgClass } from "@angular/common";
import {
    AfterContentChecked,
    Component,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { TabComponent } from "@components/tabs/tab/tab.component";
import { TabsComponent } from "@components/tabs/tabs.component";
import { TogglePanelComponent as TogglePanelComponent_1 , TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import _ from "lodash";
import { Observable, Subject, Subscription } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { SpatialService } from "~app/common/components/map/services/spatial.service";

import { SkogsvardAtgardsGrupp, SkogsvardAtgardsTyp } from "~app/common/models/constants";
import { EntityDTO } from "~app/common/models/entity-models";
import { EntityType } from "~app/common/services/cqs/models/rule-validation-message";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { canEdit } from "~app/role-permissions";
import { Atgard, SkogsvardEntity } from "../../models/atgard-models";
import { Geometri } from "../../models/karta-models";
import { AtgardMediatorService } from "../../services/atgard-mediator.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { NavigationService } from "../../services/navigation.service";
import { SkogsvardForyngringService } from "../../services/skogsvard-foryngring.service";
import { SkogsvardTradslagService } from "../../services/skogsvard-tradslag.service";
import { VirkeskopValidationService } from "../../services/validation/virkeskop-validation.service";
import { VkKartaMediatorService } from "../../services/vk-karta-mediator.service";
import { VirkeskopValidationMessageComponent } from "../../validation/validation-message/virkeskop-validation-message.component";
import { SkogsvardDetaljerComponent } from "../skogsvard-detaljer/skogsvard-detaljer.component";
import { SkogsvardForyngringComponent } from "../skogsvard-foryngring/skogsvard-foryngring.component";
import { SkogsvardHeaderComponent } from "../skogsvard-header/skogsvard-header.component";
import { SkogsvardMarkbehandlingStandortComponent } from "../skogsvard-markbehandling-standort/skogsvard-markbehandling-standort.component";
import { SkogsvardOvrigtComponent } from "../skogsvard-ovrigt/skogsvard-ovrigt.component";
import { SkogsvardTradskiktComponent } from "../skogsvard-tradskikt/skogsvard-tradskikt/skogsvard-tradskikt.component";
import { SkogsvardTradslagPlantorComponent } from "../skogsvard-tradslag-plantor/skogsvard-tradslag-plantor.component";
@Component({
    selector: "rask-skogsvard-item",
    templateUrl: "./skogsvard-item.component.html",
    styleUrls: ["./skogsvard-item.component.scss"],
    providers: [
        AtgardMediatorService,
        SkogsvardForyngringService,
        SkogsvardTradslagService
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TogglePanelComponent_1,
        NgClass,
        SkogsvardHeaderComponent,
        VirkeskopValidationMessageComponent,
        TabsComponent,
        TabComponent,
        SkogsvardDetaljerComponent,
        SkogsvardTradskiktComponent,
        SkogsvardTradslagPlantorComponent,
        SkogsvardForyngringComponent,
        SkogsvardMarkbehandlingStandortComponent,
        SkogsvardOvrigtComponent,
        AsyncPipe
    ]
})
export class SkogsvardItemComponent implements OnInit, AfterContentChecked, OnDestroy {
    highlight: boolean;
    @Input() index: number;
    @ViewChild(TogglePanelComponent) togglePanel!: TogglePanelComponent;

    protected isReadonly$: Observable<boolean>;
    protected skogsvardEntity: EntityDTO;
    protected skogsvardsAtgard: Atgard;

    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly navigationService = inject(NavigationService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly ngZone = inject(NgZone);
    private readonly spatialService = inject(SpatialService);
    private readonly validationService = inject(VirkeskopValidationService);
    private readonly vkKartaMediatorService = inject(VkKartaMediatorService);

    private navigationSubscription: Subscription;

    private readonly canEdit = canEdit();

    constructor() {
        this.isReadonly$ = this.kontraktMediatorService.isReadonly$.pipe(
            map(readonly => readonly || !this.canEdit())
        );
    }

    @Input()
    set entity(skogsvardEntity: SkogsvardEntity) {
        this.skogsvardEntity = skogsvardEntity;
        this.atgardMediatorService.initSkogsvard(skogsvardEntity);
    }

    get kontrakt$() {
        return this.kontraktMediatorService.kontrakt$;
    }

    ngAfterContentChecked(): void {
        if(!this.togglePanel || this.navigationSubscription) {
            return;
        }

        this.navigationSubscription = this.navigationService.atgardId$
            .subscribe((atgardId) => {
                if (this.togglePanel && atgardId) {
                    this.togglePanel.forceExpandedState(atgardId === this.skogsvardEntity.id);
                }
            });

        this.vkKartaMediatorService.atgardGeometriChanged$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(geometri => {
                this.updateGeometri(geometri);
            });

        this.vkKartaMediatorService.atgardGeometriDeleted$
            .pipe(takeUntil(this.ngUnsubscribe$), takeUntil(this.atgardMediatorService.removed$))
            .subscribe(geometri => this.updateGeometri(geometri));
    }

    ngOnDestroy(): void {
        this.navigationSubscription.unsubscribe();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.atgardMediatorService.atgard$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(atgard => this.skogsvardsAtgard = atgard);

        this.vkKartaMediatorService.atgardHoverIn$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(guid => this.setHighlight(this.skogsvardEntity.guid === guid));
        this.vkKartaMediatorService.atgardHoverOut$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(guid => this.setHighlight(false));
    }

    protected getIcon(entity: EntityType): string {
        return this.validationService.getEntitySeverity(entity, this.skogsvardsAtgard.guid);
    }

    protected namnForMarkbehandlingFlik() {
        switch(this.skogsvardsAtgard.atgardsgrupp) {
            case SkogsvardAtgardsGrupp.markberedning:
                return "Markbehandling";
            case SkogsvardAtgardsGrupp.foryngring:
                return "Ståndort";
        }

        return `Ej namnsatt`;
    }

    protected setHighlight(value: boolean) {
        this.ngZone.run(() => {
            this.highlight = value;
        });
    }

    protected showForyngring(): boolean {
        return (this.skogsvardsAtgard?.atgardsgrupp === SkogsvardAtgardsGrupp.markberedning && this.skogsvardsAtgard?.atgardstyp === SkogsvardAtgardsTyp.markberedningSadd) ||
            this.skogsvardsAtgard?.atgardstyp === SkogsvardAtgardsTyp.foryngringSadd;
    }

    protected showMarkBehandling(): boolean {
        return (this.skogsvardsAtgard?.isForyngring || this.skogsvardsAtgard?.isMarkberedning) ?? false;
    }

    protected showTradskikt(): boolean {
        return this.skogsvardsAtgard?.isRojning &&
        (this.skogsvardsAtgard?.atgardstyp === SkogsvardAtgardsTyp.slutrojning ||
            this.skogsvardsAtgard?.atgardstyp === SkogsvardAtgardsTyp.plantrojning ||
            this.skogsvardsAtgard?.atgardstyp === SkogsvardAtgardsTyp.nsRojning);
    }

    protected showTradslag(): boolean {
        return ((this.skogsvardsAtgard?.isForyngring &&
            this.skogsvardsAtgard?.atgardstyp !== SkogsvardAtgardsTyp.foryngringSadd &&
            this.skogsvardsAtgard?.atgardstyp !== SkogsvardAtgardsTyp.foryngringViltskydd) ||
            this.skogsvardsAtgard?.isPlantleverans) ?? false;
    }

    private updateGeometri(geometri: Geometri): void {
        if (geometri?.atgardGuid === this.skogsvardEntity.guid) {
            if (ObjectUtils.isNullOrUndefined(geometri?.mapGeometry?.wkt)) {
                // skogsvardsAtgard geometrin borttagen
                this.skogsvardsAtgard.nettoareal = 0;
                this.skogsvardsAtgard.geometri = null;
                this.skogsvardsAtgard.centrumKoordinat = null;
            } else {
                this.skogsvardsAtgard.nettoareal = _.round(geometri?.areal, 2);
                this.skogsvardsAtgard.geometri = geometri?.mapGeometry?.wkt;
                this.skogsvardsAtgard.centrumKoordinat = this.spatialService.getCenterPointAsWkt(geometri?.mapGeometry);
            }
            this.atgardMediatorService.update(this.skogsvardsAtgard);
        }
    }
}
